/**
 * A PARSED ACTION → ONE STEP (plan 4b). Pure. Import, Sync and permission changes are things the page does
 * AT ONCE, so the chat never runs them on its own: they come back as a `hold` the builder approves in the
 * confirm bar (ADR 0010; Q7, Q8, Q28). Parity (ADR 0009):
 *  - Import and Sync live on the entity LIST: with an entity open, the builder is told to close it first.
 *  - Import brings a standard entity's missing dependencies first; each, in turn, is one the page's own
 *    Import Dependencies cards would let the builder import (Q22).
 *  - Permissions need a SAVED entity (the page shows Role Permissions only then — Q8, story 32) and an
 *    existing role (roles are made on the Roles page — story 33).
 * ⚠ `import type` only for entity.model — see rules/entity-form-rules.ts.
 */
import type { Entity } from '../../../../model/entity.model';
import type { Crud, EntityAction, OotbInfo, PermissionChange, PermissionWord, RoleInfo } from '../zs-entity.types';
import { applyPermissionChange, isFullAccessRole, sameEntityName } from '../rules/entity-form-rules';

export type HeldAction =
  | { kind: 'import'; ids: string[]; names: string[] }
  | { kind: 'sync' }
  | { kind: 'permissions'; entityId: string; entityName: string; roleId: string; roleName: string; next: Crud | null; change: PermissionChange };

/** What the confirm bar shows and what approving runs. `opens` = an entity the chat opens first (permissions). */
export interface HoldStep {
  kind: 'hold';
  action: HeldAction;
  label: string;
  actionWord: string;
  tone: 'red' | 'blue';
  opens: Entity | null;
}

export interface ActionContext {
  entities: readonly Entity[];
  open: Entity | null;
  openSaved: Entity | null;
  /** The open entity's unsaved change count; 0 when none is open. */
  unsaved: number;
  roles: readonly RoleInfo[];
  ootb: readonly OotbInfo[];
  /** The page's Role Permissions or Add Role modal is open. */
  permissionsOpen: boolean;
}

export type ActionStep =
  | HoldStep
  | { kind: 'switch'; from: string; to: string; unsaved: number }
  | { kind: 'ask'; question: string; options: string[] }
  | { kind: 'answer'; text: string }
  | { kind: 'decline'; text: string; gaps: string[] }
  | { kind: 'blocked'; text: string };

export const SYNC_LABEL = 'Sync: checks every entity and builds what is missing';
export const PERMISSIONS_OPEN_TEXT = 'Finish or close Role Permissions first, then send that again.';
export const NO_ROLE_TEXT = (role: string): string =>
  `There's no role called ${role}. Roles are created on the Roles page; once one exists, I can give it permissions here.`;
export const SAVE_FIRST_TEXT = (name: string): string => `Save ${name} first — permissions need a saved entity.`;
export const CLOSE_FIRST_TEXT = (name: string, what: 'Import' | 'Sync'): string =>
  `Close ${name} first — ${what} works from the entity list.`;
export const FULL_ACCESS_TEXT = (name: string): string =>
  `${name} already has full access to every entity — its permissions can't be changed.`;

const NONE: Crud = { create: false, read: false, update: false, delete: false };
const WORDS: PermissionWord[] = ['create', 'read', 'update', 'delete'];
const andList = (xs: string[]): string => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`);
const sameCrud = (a: Crud, b: Crud): boolean => WORDS.every((w) => !!a[w] === !!b[w]);
const ordered = (ps: readonly PermissionWord[]): PermissionWord[] => WORDS.filter((w) => ps.includes(w));
const decline = (text: string): ActionStep => ({ kind: 'decline', text, gaps: [] });
/**
 * An exact (case-insensitive) name first; the form's cleaned-name match only when nothing is exact. The cleanup
 * strips "system" and "ootb", so on its own it would let `user` find `systemOotbUser`.
 */
const findByName = <T extends { name: string }>(xs: readonly T[], name: string | null): T | undefined =>
  xs.find((x) => x.name.toLowerCase() === (name ?? '').toLowerCase()) ?? xs.find((x) => sameEntityName(x.name, name));

/** How an action is named when it rode along with edits and was not done (it needs its own request). */
export function actionPhrase(a: EntityAction): string {
  const on = a.entity ? ` on ${a.entity}` : '';
  switch (a.op) {
    case 'import': return `Importing ${a.entity}`;
    case 'sync': return 'Sync';
    case 'grant': return `Giving ${a.role} ${a.permissions.length ? andList(ordered(a.permissions)) : 'access'}${on}`;
    case 'revoke': return `Taking ${andList(ordered(a.permissions))} from ${a.role}${on}`;
    case 'remove_role': return `Removing ${a.role}${a.entity ? ` from ${a.entity}` : ''}`;
  }
}

/** The standard entities to import for `item`: its missing dependencies first (depth-first), `item` last. */
export function importOrder(item: OotbInfo, ootb: readonly OotbInfo[]): OotbInfo[] {
  const out: OotbInfo[] = [];
  const seen = new Set<string>();
  const visit = (o: OotbInfo): void => {
    if (seen.has(o.id)) return;
    seen.add(o.id);
    for (const d of o.deps) {
      const dep = ootb.find((x) => x.id === d);
      if (dep && !dep.imported) visit(dep);
    }
    out.push(o);
  };
  visit(item);
  return out;
}

export function routeAction(a: EntityAction, ctx: ActionContext): ActionStep {
  const visible = ctx.entities.filter((e) => !e.hidden);

  if (a.op === 'import' || a.op === 'sync') {
    if (ctx.open) return decline(CLOSE_FIRST_TEXT(ctx.open.name || 'the new entity', a.op === 'sync' ? 'Sync' : 'Import'));
    if (a.op === 'sync') {
      return { kind: 'hold', action: { kind: 'sync' }, label: SYNC_LABEL, actionWord: 'Sync', tone: 'blue', opens: null };
    }
    const item = findByName(ctx.ootb, a.entity);
    if (!item) {
      const offered = ctx.ootb.filter((o) => !o.imported).map((o) => o.name);
      return decline(`There's no standard entity called ${a.entity}.${offered.length ? ` You can import: ${offered.join(', ')}.` : ''}`);
    }
    if (item.imported) return { kind: 'answer', text: `${item.name} is already imported.` };
    const plan = importOrder(item, ctx.ootb);
    const deps = plan.slice(0, -1).map((o) => o.name);
    return {
      kind: 'hold',
      action: { kind: 'import', ids: plan.map((o) => o.id), names: plan.map((o) => o.name) },
      label: deps.length
        ? `Import ${item.name} and ${deps.length} ${deps.length === 1 ? 'dependency' : 'dependencies'}: ${deps.join(', ')}`
        : `Import ${item.name}`,
      actionWord: 'Import',
      tone: 'blue',
      opens: null,
    };
  }

  // grant / revoke / remove_role — the page's Role Permissions modal, on a saved entity.
  if (ctx.permissionsOpen) return { kind: 'blocked', text: PERMISSIONS_OPEN_TEXT };
  // Plan 4c final review (I2) — the built-in admin is answered first: no switch question, no "which entity?", no
  // "save it first" for a change that could never be saved anyway.
  const namedRole = ctx.roles.find((r) => r.name.toLowerCase() === (a.role ?? '').toLowerCase());
  if (namedRole && isFullAccessRole(namedRole)) return { kind: 'answer', text: FULL_ACCESS_TEXT(namedRole.name) };
  let entity: Entity;
  let opens: Entity | null = null;
  // null = the open entity. A named one is the open entity when it IS that entity (same id), or — for a new,
  // never-saved entity that is not in the list yet — when its name matches.
  const saved = a.entity === null ? undefined : findByName(visible, a.entity);
  const isOpen = !!ctx.open && (a.entity === null
    || (saved ? !!saved.id && saved.id === ctx.open.id : sameEntityName(ctx.open.name, a.entity)));
  if (isOpen) {
    entity = ctx.open as Entity;
  } else if (a.entity === null) {
    return { kind: 'ask', question: 'Which entity do you mean?', options: visible.slice(0, 6).map((e) => e.name) };
  } else {
    if (!saved) return decline(`There's no entity called ${a.entity}.`);
    if (ctx.open && ctx.unsaved > 0) {
      return { kind: 'switch', from: ctx.open.name || 'the new entity', to: saved.name, unsaved: ctx.unsaved };
    }
    entity = saved;
    opens = saved;
  }
  if (!entity.id) return decline(SAVE_FIRST_TEXT(entity.name || 'the new entity'));
  const role = ctx.roles.find((r) => r.name.toLowerCase() === (a.role ?? '').toLowerCase());
  if (!role) return decline(NO_ROLE_TEXT(a.role ?? ''));
  // Plan 4c (Q37) — the built-in admin can already do everything; the server refuses any change to it.
  // Since the final review (I2) the built-in admin returns above; kept as a guard.
  if (isFullAccessRole(role)) return { kind: 'answer', text: FULL_ACCESS_TEXT(role.name) };
  const current = role.permissions?.[entity.id] ?? null;
  const perms = ordered(a.permissions);
  const hold = (next: Crud | null, label: string, actionWord: string, tone: 'red' | 'blue'): HoldStep => ({
    kind: 'hold',
    action: {
      kind: 'permissions', entityId: entity.id, entityName: entity.name, roleId: role.id, roleName: role.name, next,
      // Plan 4b fix round 1 — the approved change itself, so the page applies it to the row as it is at approval.
      change: { op: a.op as 'grant' | 'revoke' | 'remove_role', permissions: perms },
    },
    label, actionWord, tone, opens,
  });

  if (a.op === 'grant') {
    // Was (2026-10-03, plan 4b fix round 1 — hand loop; applyPermissionChange is now the single source):
    // const next: Crud = { ...NONE, ...(current ?? {}) };
    // for (const p of perms) next[p] = true;
    const next = applyPermissionChange(current, { op: 'grant', permissions: perms }) as Crud;
    if (current && sameCrud(next, current)) {
      return { kind: 'answer', text: perms.length ? `${role.name} can already ${andList(perms)} ${entity.name}.` : `${role.name} is already on ${entity.name}.` };
    }
    return perms.length
      ? hold(next, `Grant ${role.name} ${andList(perms)} on ${entity.name}`, 'Grant', 'blue')
      : hold(next, `Add ${role.name} to ${entity.name} (no permissions yet)`, 'Add', 'blue');
  }
  if (!current) {
    return { kind: 'answer', text: a.op === 'remove_role' ? `${role.name} isn't on ${entity.name}.` : `${role.name} has no access to ${entity.name}.` };
  }
  if (a.op === 'revoke') {
    // Was (2026-10-03, plan 4b fix round 1 — hand loop; applyPermissionChange is now the single source):
    // const next: Crud = { ...NONE, ...current };
    // for (const p of perms) next[p] = false;
    const next = applyPermissionChange(current, { op: 'revoke', permissions: perms }) as Crud;
    if (sameCrud(next, current)) return { kind: 'answer', text: `${role.name} already can't ${andList(perms)} ${entity.name}.` };
    return hold(next, `Revoke ${role.name}'s ${andList(perms)} on ${entity.name}`, 'Revoke', 'red');
  }
  return hold(null, `Remove ${role.name} from ${entity.name} — all its permissions there are cleared`, 'Remove', 'red');
}
